import React, { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Spinner,
} from '@heroui/react';
import { CustomButton } from '@components/ui/button/CustomButton';
import CheckCircleIcon from '@assets/icons/CheckCircleIcon';
import type { LegalVerificationStatus } from '@/types/auth';
import { getLegalSdkSupport } from '@utils/legalSdk';
import { cn } from '@utils/cn';
import { LegalVerificationSdk } from './LegalVerificationSdk';
import { PANEL_VERIFICATION_CARD_CLASSES } from './panelVerificationCard';

// ─── Design system ────────────────────────────────────────────────────────────
// «Modal Activación» (Figma 5728:14538). No usa `CustomModal`.
//
// En la verificación, **la tarjeta del modal la dibuja el SDK de Legal**: su
// shadow DOM trae fondo, radio, sombra, título, pasos (Documentos → Firmantes →
// Contrato) y pie, con un ancho máximo de 480px. Si además pintáramos nuestra
// superficie, se vería un modal dentro de otro. Por eso en esa fase el modal de
// HeroUI solo aporta el backdrop y el foco: sin fondo, sin sombra, sin header,
// sin padding y sin X (se cierra con clic afuera o Esc), con el ancho `md`
// (448px), que la tarjeta del SDK llena entera.
//
// Las fases propias (procesando, activado) conservan la superficie del panel,
// con el mismo ancho para que el modal no salte al cambiar de fase. El panel
// se activa cuando el webhook de Legal llega al backend, sin acción del usuario.

const SURFACE_CLASSNAMES = {
  base: PANEL_VERIFICATION_CARD_CLASSES,
};

const CHROMELESS_CLASSNAMES = {
  base: 'bg-transparent shadow-none',
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PanelVerificationModalProps {
  isOpen: boolean;
  /** `verification_status === 'active'` en `get-profile`. */
  isActive: boolean;
  /**
   * Esperando que el webhook active el panel: el SDK terminó en esta sesión o
   * el perfil dice `completed`. Lo calcula el proveedor; no es estado del panel.
   */
  isProcessing: boolean;
  /** La espera pasó su tope: se dejó de consultar y se remite a soporte. */
  isActivationDelayed: boolean;
  /** Último estado del webhook según `get-profile`; `null` sin trámite. */
  legalStatus: LegalVerificationStatus | null;
  /** Owner o super admin: puede abrir un trámite nuevo tras `closed`. */
  canVerify: boolean;
  onClose: () => void;
  /** Pista de `onStatusChange` del SDK, solo para la UI. */
  onStatusChange: (status: string) => void;
  /** `onComplete` del SDK: arranca la espera de la activación en el proveedor. */
  onComplete: () => void;
  /** Vuelve a consultar tras agotar la espera. */
  onRetryActivationCheck: () => void;
  /** Consulta `get-profile`; no lanza ni alerta (lo garantiza el proveedor). */
  refreshProfile: () => Promise<void>;
}

type ModalPhase = 'verification' | 'processing' | 'activated';

// ─── Component ────────────────────────────────────────────────────────────────

export const PanelVerificationModal: React.FC<PanelVerificationModalProps> = ({
  isOpen,
  isActive,
  isProcessing,
  isActivationDelayed,
  legalStatus,
  canVerify,
  onClose,
  onStatusChange,
  onComplete,
  onRetryActivationCheck,
  refreshProfile,
}) => {
  // Estado de la vista, no del negocio: el trámite vive en Legal y su estado
  // lo trae `get-profile`. `sdkMountKey` vuelve a montar el SDK (y pide otro
  // primer token) solo cuando cambia el tipo de trámite.
  const [isNewVerificationRequested, setIsNewVerificationRequested] =
    useState(false);
  const [sdkMountKey, setSdkMountKey] = useState(0);

  // El trámite nuevo es una decisión de esta apertura: se olvida al cerrar el
  // modal y en cuanto el perfil deja de decir `closed` (el webhook ya movió el
  // trámite nuevo). Si sobreviviera, reabrir pediría otro `new_verification` y
  // el backend respondería 1189. Ajuste de estado al cambiar las props, sin
  // efecto (patrón de la documentación de React).
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevLegalStatus, setPrevLegalStatus] = useState(legalStatus);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) setIsNewVerificationRequested(false);
  }
  if (legalStatus !== prevLegalStatus) {
    setPrevLegalStatus(legalStatus);
    if (legalStatus !== 'closed') setIsNewVerificationRequested(false);
  }

  let phase: ModalPhase = 'verification';
  if (isActive) phase = 'activated';
  else if (isProcessing) phase = 'processing';

  const startNewVerification = (): void => {
    setIsNewVerificationRequested(true);
    setSdkMountKey((key) => key + 1);
  };

  // 1189: el último trámite ya no está `closed` (el perfil iba atrasado). Se
  // refresca y se vuelve a montar sobre el trámite en curso.
  const handleNewVerificationRejected = (): void => {
    setIsNewVerificationRequested(false);
    setSdkMountKey((key) => key + 1);
    void refreshProfile();
  };

  const handleAlreadyActive = (): void => {
    void refreshProfile();
    onClose();
  };

  const support = getLegalSdkSupport();

  const canStartNewVerification =
    phase === 'verification' &&
    legalStatus === 'closed' &&
    canVerify &&
    !isNewVerificationRequested;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      placement="center"
      backdrop="opaque"
      size="md"
      scrollBehavior="outside"
      hideCloseButton={phase === 'verification'}
      classNames={
        phase === 'verification' ? CHROMELESS_CLASSNAMES : SURFACE_CLASSNAMES
      }
    >
      {/* Nombre fijo en todas las fases: ninguna usa `ModalHeader` (el título
          de la verificación lo pone el SDK) y los títulos visibles van dentro
          del cuerpo, sin relación con el diálogo. */}
      <ModalContent aria-label="Verificación del panel corporativo">
        {phase === 'verification' && (
          // Sin `ModalHeader`: el título visible es el del SDK.
          <ModalBody className="flex flex-col gap-4 p-0">
            {canStartNewVerification && (
              <div
                className={cn(
                  PANEL_VERIFICATION_CARD_CLASSES,
                  'flex flex-col gap-3 p-4',
                )}
              >
                <p className="text-body-2 text-fg-secondary">
                  Tu trámite anterior se cerró. Puedes iniciar uno nuevo cuando
                  quieras.
                </p>
                <CustomButton
                  label="Iniciar un nuevo trámite"
                  hierarchy="secondary"
                  onPress={startNewVerification}
                />
              </div>
            )}
            <LegalVerificationSdk
              key={sdkMountKey}
              newVerification={isNewVerificationRequested}
              onStatusChange={onStatusChange}
              onComplete={onComplete}
              onAlreadyActive={handleAlreadyActive}
              onNewVerificationRejected={handleNewVerificationRejected}
            />
          </ModalBody>
        )}

        {phase === 'processing' && isActivationDelayed && (
          <>
            <ModalBody className="flex flex-col items-center gap-4 p-10 text-center">
              <div className="flex flex-col gap-2">
                <h2 className="text-title-2 text-fg-primary">
                  La activación está tardando más de lo normal
                </h2>
                <p className="text-body-2 text-fg-secondary">
                  Tu verificación está completa, pero tu panel aún no aparece
                  activo. Vuelve a consultar en unos minutos o contacta a
                  soporte.
                </p>
              </div>
            </ModalBody>
            <ModalFooter className="flex flex-col gap-3 bg-surface-subtle p-6 sm:flex-row">
              <CustomButton
                label="Volver a consultar"
                className="w-full"
                onPress={onRetryActivationCheck}
              />
              {support?.url && (
                <CustomButton
                  label="Contactar a soporte"
                  hierarchy="secondary"
                  className="w-full"
                  as="a"
                  href={support.url}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              )}
            </ModalFooter>
          </>
        )}

        {phase === 'processing' && !isActivationDelayed && (
          <ModalBody className="flex flex-col items-center gap-4 p-10 text-center">
            <Spinner size="lg" color="primary" />
            <div className="flex flex-col gap-2">
              <h2 className="text-title-2 text-fg-primary">
                Estamos activando tu panel
              </h2>
              <p className="text-body-2 text-fg-secondary">
                Tu verificación está completa. En unos minutos tu panel quedará
                activo; no tienes que hacer nada más.
              </p>
            </div>
          </ModalBody>
        )}

        {phase === 'activated' && (
          <>
            <ModalBody className="flex flex-col items-center gap-4 p-10 text-center">
              <CheckCircleIcon
                className="size-10 shrink-0 text-icon-success"
                aria-hidden
              />
              <div className="flex flex-col gap-2">
                <h2 className="text-title-2 text-fg-primary">
                  ¡Tu panel está activo!
                </h2>
                <p className="text-body-2 text-fg-secondary">
                  Ya puedes usar todas las funciones del panel.
                </p>
              </div>
            </ModalBody>
            <ModalFooter className="bg-surface-subtle p-6">
              <CustomButton
                label="Empezar"
                className="w-full"
                onPress={onClose}
              />
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
